import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { SwapParameters, Trade, ETHER, JSBI } from '@pancakeswap/sdk'
import { useTranslation } from 'contexts/Localization'
import { useAccount, useNetwork } from 'wagmi'
import { getChainId } from 'config/constants/exchange'
import { useMemo } from 'react'
import { useGasPrice } from 'state/user/hooks'
import truncateHash from 'utils/truncateHash'
import { INITIAL_ALLOWED_SLIPPAGE } from '../config/constants'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, isAddress } from '../utils'
import isZero from '../utils/isZero'
import { useSwapCallArguments } from './useSwapCallArguments'
import { transactionErrorToUserReadableMessage } from '../utils/transactionErrorToUserReadableMessage'
import { useEthersSigner } from './useEthersProvider'
import { getRouterContractv1 } from 'utils/exchange'
import { customTokenList } from 'config/constants/exchange'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID,
}

interface SwapCall {
  contract: Contract
  parameters: SwapParameters
}

interface SuccessfulCall extends SwapCallEstimate {
  gasEstimate: BigNumber
}

interface FailedCall extends SwapCallEstimate {
  error: string
}

interface SwapCallEstimate {
  call: SwapCall
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

// Check if trade involves any custom token — if so, route through MGPRouter
const isCustomTrade = (trade: Trade, chainId: number): boolean => {
  const list = customTokenList[chainId] ?? []
  const inputAddress = (trade.inputAmount.currency as any)?.address?.toLowerCase()
  const outputAddress = (trade.outputAmount.currency as any)?.address?.toLowerCase()
  return list.some(
    (t) =>
      t.address.toLowerCase() === inputAddress ||
      t.address.toLowerCase() === outputAddress,
  )
}

// Build MGPRouter.swap() args from a Trade object
const buildCustomSwapArgs = (
  trade: Trade,
  allowedSlippage: number,
  recipient: string,
  deadline: number,
  chainId: number,
): { args: any[]; value: string } => {
  const inputCurrency = trade.inputAmount.currency
  const outputCurrency = trade.outputAmount.currency

  const tokenIn =
    inputCurrency === ETHER
      ? ZERO_ADDRESS
      : (inputCurrency as any).address

  const tokenOut =
    outputCurrency === ETHER
      ? ZERO_ADDRESS
      : (outputCurrency as any).address

  const amountIn = trade.inputAmount.raw.toString()

  // JSBI arithmetic for amountOutMin
  const slippageFactor = JSBI.BigInt(10000 - allowedSlippage)
  const denominator = JSBI.BigInt(10000)
  const amountOutMin = JSBI.divide(
    JSBI.multiply(trade.outputAmount.raw, slippageFactor),
    denominator,
  ).toString()

  const value = inputCurrency === ETHER ? amountIn : '0'

  return {
    args: [tokenIn, tokenOut, amountIn, amountOutMin, recipient, deadline],
    value,
  }
}

export function useSwapCallback(
  trade: Trade | undefined,
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE,
  recipientAddress: string | null,
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { address: account } = useAccount()
  const { chain } = useNetwork()
  const chainId = getChainId(chain)
  const library = useEthersSigner()
  const gasPrice = useGasPrice()

  const swapCalls = useSwapCallArguments(trade, allowedSlippage, recipientAddress)

  const { t } = useTranslation()
  const addTransaction = useTransactionAdder()
  const recipient = recipientAddress === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !library || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }
    if (!recipient) {
      if (recipientAddress !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: 'Invalid recipient' }
      }
      return { state: SwapCallbackState.LOADING, callback: null, error: null }
    }

    // ── Custom token trade → use MGPRouter.swap() ─────────────────────────
    if (isCustomTrade(trade, chainId)) {
      return {
        state: SwapCallbackState.VALID,
        callback: async function onSwap(): Promise<string> {
          const mgpRouter = getRouterContractv1(chainId, library)
          const deadline = Math.floor(Date.now() / 1000) + 60 * 20 // 20 min

          const { args, value } = buildCustomSwapArgs(
            trade,
            allowedSlippage,
            recipient,
            deadline,
            chainId,
          )

          const options = value && !isZero(value) ? { value } : {}

          console.log('MGPRouter.swap args:', args)
          console.log('MGPRouter.swap options:', options)

          // Estimate gas
          const gasEstimate = await mgpRouter.estimateGas
            .swap(...args, options)
            .catch(async (gasError: any) => {
              console.error('Gas estimate failed', gasError)
              // Try callStatic to surface the revert reason
              await mgpRouter.callStatic
                .swap(...args, options)
                .catch((callError: any) => {
                  throw new Error(transactionErrorToUserReadableMessage(callError, t))
                })
              throw new Error(t('Unexpected issue with estimating the gas. Please try again.'))
            })

          return mgpRouter
            .swap(...args, {
              gasLimit: calculateGasMargin(gasEstimate),
              gasPrice,
              from: account,
              ...options,
            })
            .then((response: any) => {
              const inputSymbol = trade.inputAmount.currency.symbol
              const outputSymbol = trade.outputAmount.currency.symbol
              const inputAmount = trade.inputAmount.toSignificant(3)
              const outputAmount = trade.outputAmount.toSignificant(3)

              const base = `Swap ${inputAmount} ${inputSymbol} for ${outputAmount} ${outputSymbol}`
              const withRecipient =
                recipient === account
                  ? base
                  : `${base} to ${recipientAddress && isAddress(recipientAddress)
                    ? truncateHash(recipientAddress)
                    : recipientAddress
                  }`

              addTransaction(response, { summary: withRecipient, type: 'swap' })
              return response.hash
            })
            .catch((error: any) => {
              if (error?.code === 4001) throw new Error('Transaction rejected.')
              console.error('MGPRouter swap failed', error, args, value)
              throw new Error(
                t('Swap failed: %message%', {
                  message: transactionErrorToUserReadableMessage(error, t),
                }),
              )
            })
        },
        error: null,
      }
    }

    // ── Normal PancakeSwap trade → original logic untouched ───────────────
    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const estimatedCalls: SwapCallEstimate[] = await Promise.all(
          swapCalls.map((call) => {
            const {
              parameters: { methodName, args, value },
              contract,
            } = call

            const options = !value || isZero(value) ? {} : { value }

            return contract.estimateGas[methodName](...args, options)
              .then((gasEstimate) => ({ call, gasEstimate }))
              .catch((gasError) => {
                console.error('Gas estimate failed, trying eth_call to extract error', call)
                return contract.callStatic[methodName](...args, options)
                  .then((result) => {
                    console.error('Unexpected successful call after failed estimate gas', call, gasError, result)
                    return { call, error: t('Unexpected issue with estimating the gas. Please try again.') }
                  })
                  .catch((callError) => {
                    console.error('Call threw error', call, callError)
                    return { call, error: transactionErrorToUserReadableMessage(callError, t) }
                  })
              })
          }),
        )

        const successfulEstimation = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1]),
        )

        if (!successfulEstimation) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw new Error(errorCalls[errorCalls.length - 1].error)
          throw new Error(t('Unexpected error. Could not estimate gas for the swap.'))
        }

        const {
          call: {
            contract,
            parameters: { methodName, args, value },
          },
          gasEstimate,
        } = successfulEstimation

        return contract[methodName](...args, {
          gasLimit: calculateGasMargin(gasEstimate),
          gasPrice,
          ...(value && !isZero(value) ? { value, from: account } : { from: account }),
        })
          .then((response: any) => {
            const inputSymbol = trade.inputAmount.currency.symbol
            const outputSymbol = trade.outputAmount.currency.symbol
            const inputAmount = trade.inputAmount.toSignificant(3)
            const outputAmount = trade.outputAmount.toSignificant(3)

            const base = `Swap ${inputAmount} ${inputSymbol} for ${outputAmount} ${outputSymbol}`
            const withRecipient =
              recipient === account
                ? base
                : `${base} to ${recipientAddress && isAddress(recipientAddress)
                  ? truncateHash(recipientAddress)
                  : recipientAddress
                }`

            addTransaction(response, { summary: withRecipient, type: 'swap' })
            return response.hash
          })
          .catch((error: any) => {
            if (error?.code === 4001) throw new Error('Transaction rejected.')
            console.error(`Swap failed`, error, methodName, args, value)
            throw new Error(
              t('Swap failed: %message%', {
                message: transactionErrorToUserReadableMessage(error, t),
              }),
            )
          })
      },
      error: null,
    }
  }, [trade, library, account, chainId, recipient, recipientAddress, swapCalls, gasPrice, t, addTransaction])
}